import { Checkbox, Chip, Link, TableCell, TableRow, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { Can } from 'config/ability'
import { axiosPost } from 'connectors/axiosPost'
import { useAlert } from 'context/AlertContext'
import { useAppContext } from 'context/AppContext'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from 'context/AuthContext'
import { getTimezoneOffset } from 'date-fns-tz'
import { Control, Controller } from 'react-hook-form'
import ActionDropdown from 'components/ActionDropdown'
import { Trans, useTranslation } from 'react-i18next'
import { Market } from 'types/Market'
import { SelectedValues } from '../Markets'
import { renderCells } from 'utils/renderCells'
import { Copy } from 'assets/icons/copy'
import { PenIcon } from 'assets/icons/pen'
import { KeyIcon } from 'assets/icons/key-icon'
import { format } from 'date-fns'
import { LabelBox } from 'components/label-box'

const statusBadge = (status: string): string | JSX.Element => {
  switch (status) {
    case 'live':
      return <Chip variant="outlined" color="success" label={<Trans i18nKey={`market.statuses.${status}`} />} />
    case 'holding':
      return <Chip variant="outlined" color="warning" label={<Trans i18nKey={`market.statuses.${status}`} />} />
    case 'closed':
      return <Chip variant="outlined" color="error" label={<Trans i18nKey={`market.statuses.${status}`} />} />
    case 'temporaryClosed':
      return <Chip variant="outlined" color="warning" label={<Trans i18nKey={`market.statuses.${status}`} />} />
    case 'empty':
      return <Chip variant="outlined" color="error" label={<Trans i18nKey={`market.statuses.${status}`} />} />
    default:
      return status
  }
}

type MarketRowProps = Market & { idx: number; control: Control<SelectedValues>; handleDelete: (id: string) => void }

export const MarketRow = ({ anonymization, id, flow, name, control, handleDelete, ...row }: MarketRowProps) => {
  const { token, refreshToken, get2fa } = useAuthContext()
  const { handleRefetch } = useAppContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { changeMessage } = useAlert()
  const { t } = useTranslation()

  const { mutate: anonymize, reset: resetAnonymize } = useMutation(
    async ({ id, tfa }: { id: string; tfa: string }) =>
      await axiosPost<AxiosResponse<string>>(token, refreshToken, `markets/anonymize/${id}`, { tfa }),
    {
      onSuccess: () => {
        void queryClient.refetchQueries(['markets-query'])
        handleRefetch()
      },
      onSettled: data => {
        if (data !== undefined && typeof data === 'string' && data === '') {
          changeMessage(t('common.success'), 'success', resetAnonymize)
        }
        if (data !== undefined && 'error' in data && data.error !== undefined) {
          changeMessage(data?.error?.data?.message, 'error', resetAnonymize)
        }
      },
    }
  )

  const { mutate: duplicate, reset: resetDuplicate } = useMutation(
    async ({ id }: { id: string }) =>
      await axiosPost<AxiosResponse<string>>(token, refreshToken, `markets/duplicate/${id}`, {}),
    {
      onSuccess: () => {
        void queryClient.refetchQueries(['markets-query'])
        handleRefetch()
      },
      onSettled: data => {
        if (data !== undefined && typeof data === 'string' && data === '') {
          changeMessage(t('common.success'), 'success', resetDuplicate)
        }
        if (data !== undefined && 'error' in data && data.error !== undefined) {
          changeMessage(data?.error?.data?.message, 'error', resetDuplicate)
        }
      },
    }
  )

  return (
    <TableRow
      sx={{
        position: 'relative',
        opacity: anonymization ? 0.5 : 1,
      }}
    >
      <Can I="migrate" a="Markets">
        <TableCell>
          <Controller
            name="marketIds"
            control={control}
            render={({ field: { value, ref, ...field } }) => (
              <Checkbox
                disabled={anonymization}
                {...field}
                value={id}
                inputRef={ref}
                checked={value.includes(id.toString())}
                onChange={(_, checked) => {
                  if (checked) {
                    field.onChange([...value, id.toString()])
                  } else {
                    field.onChange([...value].filter(e => e !== id.toString()))
                  }
                }}
                color="primary"
                disableRipple
              />
            )}
          />
        </TableCell>
      </Can>
      {renderCells([
        { label: 'ID', value: <LabelBox first={`#${id}`} /> },
        {
          label: t('common.name'),
          value: (
            <>
              <LabelBox
                first={
                  <strong>
                    <Can I="create" a="Markets">
                      <Link href={`/markets/edit/${id}`}>{name}</Link>
                    </Can>
                    <Can I="create" a="Markets" not>
                      <Link href={`/applications?marketId[]=${id}`}>{name}</Link>
                    </Can>
                  </strong>
                }
                second={row.description}
              />
            </>
          ),
        },
        {
          label: t('common.type'),
          value: (
            <Typography fontWeight={500} color="grey.900" variant="body2">
              {flow.toUpperCase()}
            </Typography>
          ),
        },
        { label: t('common.status'), value: statusBadge(row.status) },
        {
          label: t('common.languages'),
          value: row.languages?.length > 0 ? row.languages?.map(el => el.name).join(', ') : 'N/A',
        },
        {
          label: t('market.timezone'),
          value: (
            <>
              <Typography fontWeight={500} color="grey.900" variant="body2">
                {row.timeZone}
              </Typography>
              <Typography display="block" fontWeight={400} color="grey.600" variant="body2">
                (GMT {getTimezoneOffset(row.timeZone) / 3600000}h)
              </Typography>
            </>
          ),
        },
        {
          label: t('common.promoLiveDate'),
          value: (
            <LabelBox
              first={format(new Date(row.promoLiveDate), 'MMM dd, yyyy')}
              second={format(new Date(row.promoLiveDate), 'HH:mm:ss')}
            />
          ),
        },
        {
          label: t('common.promoEndDate'),
          value: (
            <LabelBox
              first={format(new Date(row.promoEndDate), 'MMM dd, yyyy')}
              second={format(new Date(row.promoEndDate), 'HH:mm:ss')}
            />
          ),
        },
        {
          label: t('common.holdingPageDate'),
          value: (
            <LabelBox
              first={format(new Date(row.holdingPageDate), 'MMM dd, yyyy')}
              second={format(new Date(row.holdingPageDate), 'HH:mm:ss')}
            />
          ),
        },
        {
          label: t('common.closedPageEndDate'),
          value: (
            <LabelBox
              first={format(new Date(row.closedPageEndDate), 'MMM dd, yyyy')}
              second={format(new Date(row.closedPageEndDate), 'HH:mm:ss')}
            />
          ),
        },
        {
          label: t('market.personalDataDeletedBy'),
          value: (
            <LabelBox
              first={format(new Date(row.personalDataDeletedBy), 'MMM dd, yyyy')}
              second={format(new Date(row.personalDataDeletedBy), 'HH:mm:ss')}
            />
          ),
        },
        { label: t('common.applications'), value: <LabelBox second={row.applications} /> },
        {
          label: t('application.emailVerification'),
          value: <LabelBox second={row.EmailVerification} />,
        },
        {
          label: t('application.reciptValidation'),
          value: <LabelBox second={row.ReciptValidation} />,
        },
        { label: t('application.accepted'), value: <LabelBox second={row.Accepted} /> },
        {
          label: t('application.fraud'),
          value: <LabelBox second={row.Fraud} />,
        },
        { label: t('application.rejected'), value: <LabelBox second={row.Rejected} /> },
        {
          label: t('application.prizeDrawWinner'),
          value: flow === 'pd' ? row.PrizeDrawWinner : 'N/A',
        },
        {
          label: t('application.prizeDrawLoser'),
          value: flow === 'pd' ? row.PrizeDrawLoser : 'N/A',
        },
      ])}
      <ActionDropdown
        subject="Markets"
        name={name}
        buttons={[
          {
            label: t('common.edit'),
            name: 'edit-market',
            handler: (): void => {
              navigate(`/markets/edit/${id}`, { state: { ...row } })
            },
            icon: <PenIcon />,
          },
          {
            label: t('common.duplicate'),
            name: 'duplicate-market',
            handler: (): void => {
              duplicate({ id: id.toString() })
            },
            icon: <Copy />,
          },
          {
            label: t('common.anonymize'),
            name: 'anonymize-market',
            handler: (): void => {
              void get2fa(async tfa => {
                anonymize({ id: id.toString(), tfa })
              })
            },
            icon: <KeyIcon />,
          },
        ]}
        deleteHandler={() => {
          handleDelete(id.toString())
        }}
      />
    </TableRow>
  )
}
